
import Vue, { PropType } from 'vue';
import Api from '@/core/api';
import Voted from './Voted.vue';
import Poll from './Poll.vue';
import Results from './Results.vue';
import NoPoll from './NoPoll.vue';
import WordCloud from './WordCloud.vue';
import Icon, { Icon as IconName } from '@/common/icon/Icon.vue';
import { WordCloud as WC } from '@/word-cloud/interfaces';
import { Layouts as WCLayouts } from '@/word-cloud/constants';
import { PollType } from '@/widgets/polls/polls.controller';
import { Theme } from '@/side-bar/side-bar-config.interface';

export interface PollConfig {
    allowVoting: boolean;
    showResults: boolean;
    wordCloudConfig: WC.Config;
    pollId:string
    pollType:string
    question:string
    characterLimit: number
    allowMultiWords: boolean
    answers?:Record<string, string>[]
    results?:Record<string, unknown>[]
    answerLimit?:number
    hasVoted?:boolean
    preventExpand?:boolean
    countdownEndTimestamp?:number
    countdownDuration?: number;
    showPollIncoming?: boolean;
}

export default Vue.extend({
    name: 'Polls',

    components: {
        Poll,
        Results,
        Voted,
        NoPoll,
        WordCloud,
        Icon
    },

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        },
        config: {
            type: Object as PropType<PollConfig>,
            required: true
        },
        theme: {
            type: Object as PropType<Theme>,
            default: () => ({})
        },
        expanded: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            checkIcon: IconName.check
        };
    },

    computed: {
        question():string {
            return this.config.question;
        },
        countdownEndTimestamp():number {
            return this.config.countdownEndTimestamp || -1;
        },
        countdownDuration():number {
            return this.config.countdownDuration || -1;
        },
        results():Record<string, unknown>[] {
            return this.config.results || [];
        },
        showResults(): boolean {
            return this.config.showResults;
        },
        answers():Record<string, string>[] {
            return this.config.answers || [];
        },
        answerLimit():number {
            return this.config.answerLimit || 1;
        },
        canSubmitAnswer(): boolean {
            return !!this.config.answerLimit;
        },
        hasVoted():boolean {
            return this.config.hasVoted || false;
        },
        pollId():string {
            return this.config.pollId;
        },
        pollType():string {
            return this.config.pollType || PollType.MULTIPLE_CHOICE;
        },
        characterLimit():number {
            return this.config.characterLimit || 0;
        },
        allowMultiWords():boolean {
            return this.config.allowMultiWords || false;
        },
        allowVoting():boolean {
            return this.config.allowVoting || false;
        },
        wordCloudConfig():WC.Config {
            return {
                ...(this.config?.wordCloudConfig || {}),
                layout: this.expanded ? WCLayouts.Expanded : WCLayouts.Compact,
            };
        },
        showWordCloud(): boolean {
            return this.pollType === PollType.WORD_CLOUD;
        },
        preventExpand():boolean {
            return this.config?.preventExpand || false;
        },
        showPollIncoming():boolean {
            return this.config?.showPollIncoming || false;
        },
        pollState(): 'poll' | 'results' | 'voted' | 'noPoll' {
            const hasResults = this.results && this.results.length;
            const hasAnswers = this.answers && this.answers.length;
            if (!this.pollId) {
                return 'noPoll';
            }
            if (!this.question) {
                return 'noPoll';
            }
            if (!hasAnswers && !hasResults) {
                return 'noPoll';
            }
            if (this.results && this.results.length && this.showResults) {
                return 'results';
            }
            if (this.hasVoted) {
                return 'voted';
            }
            if (!this.allowVoting) {
                return 'noPoll';
            }
            return 'poll';
        }
    },

    watch: {
        pollState(newState: string, oldState: string) {
            if (newState === 'noPoll' && oldState !== 'noPoll') {
                (this?.$refs?.wordCloud as Vue & { collapseSidebar: () => void })?.collapseSidebar();
            }
        }
    }
});
